import { useState } from "react"
import { Page } from "@/components/page"
import { useDatabaseData, usePage } from "@/components/page/hooks"
import { Combobox } from "@/components/ui/combobox"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { MessageCircle, Plus, Send, Trash2, Mic, X, ArrowUp, LoaderCircle } from "lucide-react"
import { ChatPanel, ChatMessage } from "./chat-panel"
import { ulid } from "ulid"
import { supabase } from "@/lib/data"
import { app } from "@/lib/app"
import { agents } from "@/lib/agents"
import { useVoiceRecording } from "@/hooks/use-voice-recording"

const STORAGE_KEY = "multichat-session-uuid"

export const multichat = {
  title: "Multi Chat",
  route: "/multichat",
  url: "/multichat",
  icon: MessageCircle,
  component,
  group: "dev",
  resource: "agents",
  action: "read",
} satisfies PageConfig

interface Chat {
  id: string
  agentId: string
  agentTitle: string
  messages: ChatMessage[]
  isProcessing: boolean
}

export function component() {
  const { team } = usePage()
  const { data, loading } = useDatabaseData("agents", {
    fields: ["id", "title"],
    filter: (supa) => supa.eq("team_id", team.id),
  })

  const [selectedAgent, setSelectedAgent] = useState<string | number>("")
  const [chats, setChats] = useState<Chat[]>([])
  const [inputValue, setInputValue] = useState("")
  const [sessionUuid, setSessionUuid] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || ""
  })

  const addedAgentIds = new Set(chats.map((chat) => chat.agentId))
  const items = data
    .filter((agent) => !addedAgentIds.has(String(agent.id)))
    .map((agent) => ({
      value: agent.id,
      label: agent.title,
    }))

  const handleAddChat = () => {
    if (!selectedAgent) return
    const agent = data.find((a) => String(a.id) === String(selectedAgent))
    if (!agent) return

    const newChat: Chat = {
      id: ulid(),
      agentId: String(agent.id),
      agentTitle: agent.title,
      messages: [],
      isProcessing: false,
    }
    setChats([...chats, newChat])
    setSelectedAgent("")
  }

  const handleRemoveChat = (chatId: string) => {
    setChats(chats.filter((c) => c.id !== chatId))
  }

  const getOrCreateSessionUuid = () => {
    if (sessionUuid) return sessionUuid
    const newUuid = crypto.randomUUID()
    localStorage.setItem(STORAGE_KEY, newUuid)
    setSessionUuid(newUuid)
    return newUuid
  }

  const onDeleteHistory = async () => {
    await supabase.from("states").delete().eq("id", sessionUuid)
    await supabase.from("history").delete().eq("uid", sessionUuid)
  }

  const handleDeleteHistory = () => {
    setChats((prev) => prev.map((chat) => ({ ...chat, messages: [] })))
    setSessionUuid("")
    localStorage.removeItem(STORAGE_KEY)
    onDeleteHistory()
  }

  const sendMessageToAllChats = async (message: string) => {
    if (!message.trim() || chats.length === 0) return

    getOrCreateSessionUuid()

    setChats((prev) =>
      prev.map((chat) => ({
        ...chat,
        messages: [...chat.messages, { role: "user" as const, content: message }],
        isProcessing: true,
      }))
    )

    for (const chat of chats) {
      callAgent(chat.id, chat.agentId, chat.agentTitle, message)
    }
  }

  async function callAgent(chatId: string, agentId: string, agentTitle: string, message: string) {
    // TODO: Implement actual agent call here
    // For now, simulate a response with a timeout
    console.log(agentId)
    const apiKeys = await app.fetchAPIkeys(team?.id)

    // await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 1000))

    const p: AgentParameters = {
      debug: true,
      uid: sessionUuid,
      input: { message },
      apiKeys,
    }
    const agent = await agents.loadAgent(agentId as any, team.id)
    await agent.execute(p)

    const response = p.output.response || ""
    setChats((prev) => prev.map((chat) => {
      if (chat.id !== chatId) return chat
      return {
        ...chat,
        messages: [...chat.messages, { role: "assistant" as const, content: response }],
        isProcessing: false,
      }
    })
    )
  }

  const handleSendMessage = () => {
    if (!inputValue.trim()) return
    sendMessageToAllChats(inputValue)
    setInputValue("")
  }

  const {
    isRecording,
    recordingTime,
    isTranscribing,
    formatRecordingTime,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording({
    teamId: team?.id,
    onTranscription: sendMessageToAllChats,
    disabled: chats.length === 0,
  })

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return <Page>
    <div className="flex flex-col gap-4 h-full">
      <div className="flex gap-4 items-center">
        <div className="w-96">
          <Combobox items={items} placeholder="Select an agent..." value={selectedAgent} onChange={setSelectedAgent} />
        </div>
        <Button onClick={handleAddChat} disabled={!selectedAgent} variant="outline" className="gap-2">
          <Plus className="h-4 w-4" />
          Add Chat
        </Button>
        <div className="grow" />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" disabled={chats.length === 0} className="hover:bg-red-50 cursor-pointer" title="Delete history">
              <Trash2 className="h-full w-full text-red-500 cursor-pointer" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete History</AlertDialogTitle>
              <AlertDialogDescription>
                Do you want to delete all chat history? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteHistory} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grow min-h-0">
        {chats.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400">
            Select an agent and click "Add Chat" to start
          </div>
        ) : (
          <div className="grid h-full gap-4" style={{ gridTemplateColumns: `repeat(${chats.length}, 1fr)` }}>
            {chats.map((chat) => (
              <ChatPanel
                key={chat.id}
                agentId={chat.agentId}
                agentTitle={chat.agentTitle}
                messages={chat.messages}
                isProcessing={chat.isProcessing}
                onRemove={() => handleRemoveChat(chat.id)}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 items-center">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message to send to all chats..."
          disabled={chats.length === 0 || isTranscribing}
          className="grow"
        />
        {isRecording && (
          <>
            <Button
              type="button"
              onClick={cancelRecording}
              variant="ghost"
              size="icon"
              title="Cancel recording"
            >
              <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
            </Button>
            <div className="flex items-center gap-2 px-2">
              <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-red-600 font-medium tabular-nums">
                {formatRecordingTime(recordingTime)}
              </span>
            </div>
          </>
        )}
        <Button
          type="button"
          onClick={isRecording ? stopRecording : startRecording}
          variant={isRecording ? "destructive" : "outline"}
          size="icon"
          disabled={chats.length === 0 || isTranscribing}
          title={isRecording ? "Send voice note" : "Record voice note"}
        >
          {isRecording ? (
            <ArrowUp className="h-4 w-4" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </Button>
        <Button
          onClick={handleSendMessage}
          disabled={chats.length === 0 || !inputValue.trim() || isTranscribing}
          size="icon"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
      {isTranscribing && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          <span>Transcribing…</span>
        </div>
      )}
    </div>
  </Page>
}
